import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';

import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import remixI18n from '~/i18next.server';
import ReferralsBreakdown from '~/pages/referrals/ReferralsBreakdown';
import RewardsProgress from '~/pages/referrals/RewardsProgress';
import ShareOverview from '~/pages/referrals/ShareOverview';
import getPublication from '~/services/api/publication/getPublication.server';
import getReferrals from '~/services/api/subscription/getReferrals.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';
import redirectWithToast from '~/utils/redirectWithToast/index.server';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const root: any = matches.find((match) => match.id === 'root');
  const publication = root?.data?.publication;
  const metaTitle = data?.metaTitle;

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !metaTitle,
    overrides: {
      robots: 'noindex',
      title: metaTitle,
      description: `Help the ${publication.name} community grow by sharing your referral link and earning rewards.`,
    },
  });
};

export const loader: LoaderFunction = async ({ request, params }) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);

  const [publication, referrals] = await Promise.all([
    getPublication(request, session),
    getReferrals(request, session, params?.subscriberId),
  ]);

  const locale = publication?.language || 'en';
  const t = await remixI18n.getFixedT(locale, 'common');

  if (referrals) {
    return json({
      requestUrl: url.href,
      referrals: referrals.referrals,
      rewards: referrals.rewards,
      referralCount: referrals.referral_count,
      referralLink: referrals.referral_link,
      metaTitle: t('meta.titles.referrals'),
    });
  }

  return redirect(
    redirectWithToast({
      baseUrl: '/',
      toast: 'error',
      message: `${t('errors.referrals')} ${t('errors.try_later')}`,
    }),
  );
};

interface ReferralsLoaderData {
  referrals: any;
  rewards: any;
  referralCount: number;
  referralLink: string;
}

const Referrals = () => {
  const { referrals, referralCount, rewards, referralLink } = useTypedLoaderData<ReferralsLoaderData>();

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-8">
      <ShareOverview referralLink={referralLink} referralCount={referralCount} />
      <RewardsProgress rewards={rewards} />
      <ReferralsBreakdown referrals={referrals} />
    </div>
  );
};

export default Referrals;
